// src/storage/lastVisited.js
//
// "Continue where you left off" (library-2-shelf) -- a tiny LOCAL record of the last
// section a student opened, plus a per-pack completion cache.
//
// Deliberately localStorage, not Firestore: this is a convenience pointer, not user
// data. No schema, no new reads or writes, nothing to migrate, and it costs the reader
// one synchronous set() when the selected section changes.
//
// TWO records, both written by the readers (PackReaderGrouped / PackReaderSetText):
//
//   btl.lastVisited  { packId, itemId, title, packTitle, ts }
//       The Continue card. `title` is the SECTION title, `packTitle` the pack's.
//
//   btl.packStats    { [packId]: { done, total, ts } }
//       Per-pack completion for the shelf rings. It lives here because the READER is
//       the only place that knows a pack's item total -- the dashboard's pack objects
//       carry `items: 0` ("total-item count is not on the pack doc", usePacksBridge
//       adaptPack), and reading it would mean a new Firestore read, which this slice
//       is not allowed to add. So a pack shows a ring once the student has opened it,
//       which is exactly the set of packs where a ring means anything.
//
// Every read is defensive: missing key, unparseable JSON, wrong shape and blocked
// storage (private mode, cookies off) all yield the empty answer rather than throwing.

export const LAST_VISITED_KEY = 'btl.lastVisited';
export const PACK_STATS_KEY = 'btl.packStats';

// Cap the stats map so a long-lived browser cannot grow it without bound.
const MAX_PACK_STATS = 40;

const str = (v) => (typeof v === 'string' ? v.trim() : '');
const nonNegInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

// The ambient store, or null when storage is unavailable/blocked.
function defaultStorage() {
    try {
        return typeof globalThis !== 'undefined' && globalThis.localStorage ? globalThis.localStorage : null;
    } catch { return null; }   // accessing localStorage itself can throw
}

function readJson(key, storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    if (!s) return null;
    try {
        const raw = s.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch { return null; }   // corrupt JSON / blocked read
}

function writeJson(key, value, storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    if (!s) return false;
    try { s.setItem(key, JSON.stringify(value)); return true; }
    catch { return false; }    // quota / blocked write -- the feature just goes quiet
}

/* ------------------------------------------------------------------ last visited -- */

// A record is usable only with both ids; everything else is cosmetic and defaulted.
// `done`/`total` are optional and ride along so the Continue card can show a bar
// without a second lookup.
export function normalizeLastVisited(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const packId = str(raw.packId);
    const itemId = str(raw.itemId);
    if (!packId || !itemId) return null;
    const done = nonNegInt(raw.done);
    const total = nonNegInt(raw.total);
    return {
        packId,
        itemId,
        title: str(raw.title),
        packTitle: str(raw.packTitle),
        ts: nonNegInt(raw.ts) || 0,
        ...(done !== null && total !== null && total > 0 ? { done: Math.min(done, total), total } : {}),
    };
}

export function readLastVisited(storage) {
    return normalizeLastVisited(readJson(LAST_VISITED_KEY, storage));
}

// Returns the stored record (normalized) or null when it was rejected/unwritable.
export function writeLastVisited(rec, storage) {
    const norm = normalizeLastVisited(rec);
    if (!norm) return null;
    return writeJson(LAST_VISITED_KEY, norm, storage) ? norm : null;
}

export function clearLastVisited(storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    if (!s) return;
    try { s.removeItem(LAST_VISITED_KEY); } catch { /* blocked -- nothing to do */ }
}

// The Continue card must not offer a pack the user can no longer see (unpublished,
// entitlement lapsed, catalog reordered). `packs` is the live catalog; an EMPTY or
// missing catalog means "not loaded yet", so we withhold rather than guess.
export function lastVisitedForPacks(rec, packs) {
    const norm = normalizeLastVisited(rec);
    if (!norm) return null;
    const list = Array.isArray(packs) ? packs : [];
    if (list.length === 0) return null;
    const pack = list.find((p) => p && p.id === norm.packId);
    return pack ? norm : null;
}

/* -------------------------------------------------------------------- pack stats -- */

export function readPackStats(storage) {
    const raw = readJson(PACK_STATS_KEY, storage);
    if (!raw) return {};
    const out = {};
    for (const k of Object.keys(raw)) {
        const v = raw[k];
        if (!v || typeof v !== 'object') continue;
        const done = nonNegInt(v.done);
        const total = nonNegInt(v.total);
        if (done === null || total === null || total <= 0) continue;
        out[k] = { done: Math.min(done, total), total, ts: nonNegInt(v.ts) || 0 };
    }
    return out;
}

// Record one pack's completion. Keeps the map bounded by dropping the oldest entries.
export function writePackStat(packId, stat, storage) {
    const id = str(packId);
    const done = nonNegInt(stat && stat.done);
    const total = nonNegInt(stat && stat.total);
    if (!id || done === null || total === null || total <= 0) return false;

    const map = readPackStats(storage);
    map[id] = { done: Math.min(done, total), total, ts: nonNegInt(stat.ts) || 0 };

    const keys = Object.keys(map);
    if (keys.length > MAX_PACK_STATS) {
        keys.sort((a, b) => (map[a].ts || 0) - (map[b].ts || 0));
        for (const k of keys.slice(0, keys.length - MAX_PACK_STATS)) delete map[k];
    }
    return writeJson(PACK_STATS_KEY, map, storage);
}
